import * as RadixTooltip from "@radix-ui/react-tooltip";

import { ContentProvider, useContentProvider } from "./ContentProvider";
import { PropsWithChildren, createContext, forwardRef, useContext, useMemo, useState } from "react";

import { Theme } from "react-migration/lib/theme/Theme";
import cn from "clsx";

type TooltipTriggerContextType = {
  open: boolean;
  triggerMode: "hover" | "click";
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const TooltipTriggerContext = createContext<TooltipTriggerContextType>({
  open: false,
  triggerMode: "click",
  setOpen: () => false,
});

// https://www.radix-ui.com/primitives/docs/components/tooltip#provider
type WrapperProps = Pick<RadixTooltip.TooltipProviderProps, "children"> &
  Partial<Pick<RadixTooltip.TooltipProviderProps, "delayDuration">> & {
    triggerMode?: "hover" | "click";
  };

function Wrapper({ children, triggerMode = "click", ...props }: WrapperProps) {
  const [open, setOpen] = useState<boolean>(false);

  const isControlled = triggerMode === "click";
  const providerProps = useMemo(() => ({ open, setOpen, triggerMode }), [open, setOpen, triggerMode]);

  return (
    <RadixTooltip.Provider {...props}>
      <RadixTooltip.Root
        {...(isControlled && {
          onOpenChange: setOpen,
          open,
        })}
      >
        <TooltipTriggerContext.Provider value={providerProps}>{children}</TooltipTriggerContext.Provider>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

// https://www.radix-ui.com/primitives/docs/components/tooltip#content
type ContentProps = Pick<RadixTooltip.TooltipContentProps, "children"> &
  Partial<Pick<RadixTooltip.TooltipContentProps, "align" | "side" | "sideOffset">> & {
    /** Size of tooltip.  */
    size?: "xs" | "sm" | "m" | "lg";
    /** Conditionally render tooltip arrow. */
    showArrow?: boolean;
    /** Control tooltip theme. */
    theme?: Theme;
    /** Clamp tooltip width. */
    clampWidth?: boolean | number;
    /** Style tooltip. */
    style?: React.CSSProperties;
  };

const Content = forwardRef<HTMLDivElement, ContentProps>(
  (
    {
      children,
      showArrow = true,
      theme = Theme.Light,
      sideOffset = 8,
      clampWidth = true,
      size = "m",
      ...contentProps
    }: ContentProps,
    ref
  ) => {
    const maxWidthClass = typeof clampWidth === "number" ? `max-w-[${clampWidth}px]` : undefined;
    return (
      <RadixTooltip.Content {...contentProps} sideOffset={sideOffset} className="z-50" ref={ref}>
        <ContentProvider.Provider value={{ theme }}>
          <section
            className={cn(
              "shadow-md rounded-sm text-xs text-left",
              {
                "px-2.5 py-1": size === "xs",
                "px-3 py-2": size === "sm",
                "px-5 py-3": size === "m",
                "px-6 py-4": size === "lg",
                "bg-neutral-700 text-white": theme === Theme.Dark,
                "bg-white text-text-base-primary": theme === Theme.Light,
              },
              clampWidth === true ? "max-w-xs" : maxWidthClass
            )}
          >
            {children}
            {showArrow && (
              <RadixTooltip.Arrow
                className={cn("w-3 h-1.5", {
                  "fill-neutral-700": theme === Theme.Dark,
                  "fill-white": theme === Theme.Light,
                })}
              />
            )}
          </section>
        </ContentProvider.Provider>
      </RadixTooltip.Content>
    );
  }
);
Content.displayName = "TooltipContent";

function Trigger(props: RadixTooltip.TooltipTriggerProps) {
  const { open, setOpen, triggerMode } = useContext(TooltipTriggerContext);

  if (triggerMode === "click") {
    return (
      <RadixTooltip.Trigger
        asChild
        onClick={(event) => {
          props.onClick?.(event);
          if (!open) setOpen(true);
        }}
        {...props}
      />
    );
  }
  return <RadixTooltip.Trigger asChild {...props} />;
}

function Header({ children }: PropsWithChildren) {
  return <h1 className="mb-1 text-sm font-semibold">{children}</h1>;
}

function Description({ children, className = "" }: PropsWithChildren<{ className?: string }>) {
  const { theme } = useContentProvider();

  return (
    <p
      data-testid="tooltip-description"
      className={cn(
        "m-0! text-xs",
        {
          "text-text-base-secondary": theme === Theme.Light,
          "text-neutral-200": theme === Theme.Dark,
        },
        className
      )}
    >
      {children}
    </p>
  );
}

/**
 * "Radix Powered" Tooltip component.
 * https://www.radix-ui.com/primitives/docs/components/tooltip
 *
 * 🚨 Radix components do not play nicely with Vuera in production, or the way we render React in Vue.
 * If you think this is going to be a problem, please use `components/DeprecatedTooltip` instead.
 */
export const Tooltip = {
  Wrapper,
  Content,
  Trigger,
  Header,
  Description,
  Portal: RadixTooltip.Portal,
};
